import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import {
  SignIn,
  useOrganization,
  useUser,
  UserButton,
  OrganizationSwitcher,
} from '@clerk/clerk-react'
import { useConfiguration } from '../hooks/useConfiguration'
import { useHannaEnabled } from '../hooks/useHannaEnabled'
import { useIsSuperUser } from '../hooks/useIsSuperUser'
import { ProtectedAgentRoute } from './ProtectedAgentRoute'
import { Agent } from '../agent'
import { useSelectedAgent } from '../contexts/SelectedAgentContext'
import { ENV } from '../config/env'
import careforceLogo from '@/images/brand/careforce-logo-with-title.png'
import { AlertTriangle } from 'lucide-react'

const DisabledAccess = ({ hasMultipleOrgs }: { hasMultipleOrgs: boolean }) => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
    <div className="max-w-md w-full space-y-8">
      {/* Logo */}
      <div className="flex justify-center">
        <img
          src={careforceLogo}
          alt="Careforce"
          className="h-8 w-auto select-none"
          draggable={false}
        />
      </div>

      {/* Error card */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Organization Not Configured
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              This organization does not have agent access configured. Please
              contact support to enable agents for your organization.
            </p>
          </div>
        </div>

        {/* Org switcher if user has multiple orgs */}
        {hasMultipleOrgs && (
          <div className="pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-500 mb-3">
              Switch to a different organization:
            </p>
            <OrganizationSwitcher
              appearance={{
                elements: {
                  rootBox: 'w-full',
                  organizationSwitcherTrigger:
                    'w-full justify-between border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50',
                },
              }}
            />
          </div>
        )}
      </div>

      {/* User controls */}
      <div className="flex items-center justify-center gap-4">
        <UserButton
          appearance={{
            elements: {
              avatarBox: 'w-8 h-8',
            },
          }}
          showName={true}
        />
      </div>
    </div>
  </div>
)

export function AgentSelection() {
  const { organization, isLoaded: orgLoaded } = useOrganization()
  const { isSignedIn, isLoaded, user } = useUser()
  const { configuration, loading, error } = useConfiguration()
  const { selectedAgentId } = useSelectedAgent()
  const hannaEnabled = useHannaEnabled()
  const isSuperUser = useIsSuperUser()
  const location = useLocation()

  // Check if user has multiple organizations (for showing org switcher)
  const hasMultipleOrgs = (user?.organizationMemberships?.length ?? 0) > 1
  const isCareforceMember =
    user?.organizationMemberships?.some(
      (membership) => membership.organization.slug === 'careforce'
    ) ?? false
  const shouldRedirectToProduction =
    isLoaded && isSignedIn && ENV === 'stage' && !isCareforceMember

  // Update document title when component mounts
  useEffect(() => {
    document.title = 'Careforce'
  }, [])

  useEffect(() => {
    if (!shouldRedirectToProduction) return

    const target = new URL(window.location.href)
    target.protocol = 'https:'
    target.host = 'app.careforce.ai'
    window.location.replace(target.href)
  }, [shouldRedirectToProduction])

  // Show loading spinner while Clerk is loading
  if (!isLoaded || !orgLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  // Show sign-in if not authenticated (must check before configuration)
  if (!isSignedIn) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full space-y-8">
          {/* Logo */}
          <div className="flex justify-center">
            <img
              src={careforceLogo}
              alt="Careforce"
              className="h-8 w-auto select-none"
              draggable={false}
            />
          </div>
          <SignIn />
        </div>
      </div>
    )
  }

  if (shouldRedirectToProduction) return null

  // Keep feature previews restricted to Careforce super users. The shared
  // stage (ENV==='stage') instead allows every Careforce org member.
  if (ENV === 'preview' && !isSuperUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full mx-auto text-center space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Access Restricted
            </h2>
          </div>
          <div className="pt-4">
            <UserButton
              appearance={{
                elements: {
                  avatarBox: 'w-8 h-8',
                  userButtonPopoverCard:
                    'bg-white shadow-lg border border-gray-200',
                  userButtonPopoverActionButton:
                    'text-gray-700 hover:text-gray-900 hover:bg-gray-100',
                },
              }}
              showName={true}
              userProfileMode="navigation"
              userProfileUrl="/user-profile"
            />
          </div>
        </div>
      </div>
    )
  }

  // If the user is a member of multiple organizations but there's no organization context, show the organization switcher
  if (user?.organizationMemberships?.length > 1 && !organization?.slug) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full space-y-8">
          {/* Logo */}
          <div className="flex justify-center">
            <img
              src={careforceLogo}
              alt="Careforce"
              className="h-8 w-auto select-none"
              draggable={false}
            />
          </div>

          {/* Message card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 text-center space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Select an Organization
            </h2>
            <p className="text-sm text-gray-600">
              Select an organization below to access the application.
            </p>
          </div>

          {/* Organization switcher */}
          <div className="flex items-center justify-center pt-4 border-t border-gray-100">
            <OrganizationSwitcher
              appearance={{
                elements: {
                  rootBox: 'w-full',
                  organizationSwitcherTrigger:
                    'w-full justify-between border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50',
                },
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  if (!organization?.slug) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full space-y-8">
          {/* Logo */}
          <div className="flex justify-center">
            <img
              src={careforceLogo}
              alt="Careforce"
              className="h-8 w-auto select-none"
              draggable={false}
            />
          </div>

          {/* Message card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 text-center space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">
              No Organization Found
            </h2>
            <p className="text-sm text-gray-600">
              You must be a member of an organization to access this
              application.
            </p>
          </div>

          {/* User controls */}
          <div className="flex items-center justify-center">
            <UserButton
              appearance={{
                elements: {
                  avatarBox: 'w-8 h-8',
                },
              }}
              showName={true}
            />
          </div>
        </div>
      </div>
    )
  }

  // Show loading spinner while configuration is loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  // Check if configuration failed to load or is missing
  if (error || !configuration) {
    return <DisabledAccess hasMultipleOrgs={hasMultipleOrgs} />
  }

  const davidEnabled = configuration.agents?.david?.enabled ?? false

  if (!hannaEnabled && !davidEnabled) {
    return <DisabledAccess hasMultipleOrgs={hasMultipleOrgs} />
  }

  // Hanna URL gate: when the org doesn't have Hanna, normalize the URL so
  // /?agent=hanna and the legacy /hanna path collapse to /?agent=david,
  // preserving the current tab pathname and any other query params.
  if (selectedAgentId === 'hanna' && !hannaEnabled) {
    const params = new URLSearchParams(location.search)
    params.set('agent', 'david')
    return (
      <Navigate
        to={{
          pathname: location.pathname.startsWith('/hanna')
            ? '/'
            : location.pathname,
          search: `?${params.toString()}`,
        }}
        replace
      />
    )
  }

  // Prefer the agent from SelectedAgentContext (which is already synced with
  // the URL's ?agent= parameter) when that agent is enabled. Fall back to the
  // previous configuration-based default when needed.
  const preferredAgentId =
    (selectedAgentId && configuration.agents?.[selectedAgentId]?.enabled
      ? selectedAgentId
      : undefined) ?? (davidEnabled ? 'david' : 'hanna')

  // Render the default agent app in place without redirecting, so the root
  // path ("/") shows the full Home layout + conversation sidebar.
  return (
    <ProtectedAgentRoute agentId={preferredAgentId}>
      <Agent agent={preferredAgentId} />
    </ProtectedAgentRoute>
  )
}
